/**
 * audio-capture.ts —— 麦克风采集 + 重采样（与 ASR 厂商无关）
 *
 * 目标输出：PCM 16kHz / 16bit / 单声道 —— 豆包（以及几乎所有云端 ASR）
 * 的标准输入格式。而浏览器的 AudioContext 采样率随设备而变（常见 44.1k / 48k），
 * 所以必须重采样，不能直接把原始数据丢出去。
 *
 * 两个实现路径，按能力择优：
 *   ① AudioWorklet（首选）：在音频线程里跑，不受主线程卡顿影响，不掉帧。
 *   ② ScriptProcessorNode（降级）：AudioWorklet 需要单独加载一个脚本文件，
 *      在打包环境里路径容易出问题；降级路径保证功能不丢，只是线程占用差些。
 *
 * 重采样用「线性插值」而非专业滤波：语音识别对高频毛刺不敏感，
 * 而线性插值零依赖、可预测、够快。这是有意识的取舍，不是偷懒。
 */

export interface CaptureOptions {
  /** 目标采样率，默认 16000 */
  sampleRate?: number;
  /** 每个音频分片大约多少毫秒，默认 100ms */
  chunkMs?: number;
  /** 收到一个分片时回调（PCM 16bit 小端 Int16 缓冲区） */
  onChunk: (pcm: Int16Array) => void;
}

export interface CaptureHandle {
  /** 停止采集并释放麦克风 */
  stop(): Promise<void>;
  /** 是否仍在采集 */
  readonly active: boolean;
}

/**
 * 开始采集。返回一个句柄，stop() 后不再回调。
 *
 * 失败会抛出带人话的 Error（权限拒绝 / 找不到设备 / 非安全上下文）。
 */
export async function startCapture(options: CaptureOptions): Promise<CaptureHandle> {
  const targetRate = options.sampleRate ?? 16000;
  const chunkMs = options.chunkMs ?? 100;

  if (typeof window !== 'undefined' && 'isSecureContext' in window && !window.isSecureContext) {
    throw new Error('语音输入只能在 HTTPS 或 localhost 下使用。');
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('这个浏览器不支持麦克风采集。');
  }

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
  } catch (err) {
    throw new Error(translateMicError(err));
  }

  const AudioCtx: typeof AudioContext =
    window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const ctx = new AudioCtx();
  const source = ctx.createMediaStreamSource(stream);

  const inputRate = ctx.sampleRate;
  const framesPerChunk = Math.round((targetRate * chunkMs) / 1000);

  let active = true;
  /** 累积重采样后的样本，够一帧就发 */
  let pending: number[] = [];

  /** 线性插值重采样：把 input 的若干个浮点样本映射到输出率 */
  const ratio = inputRate / targetRate;
  let readPos = 0;
  let carry: Float32Array = new Float32Array(0);

  const pushSamples = (input: Float32Array): void => {
    // 把上一轮的尾巴接上，保证插值连续
    const buf = new Float32Array(carry.length + input.length);
    buf.set(carry, 0);
    buf.set(input, carry.length);

    while (readPos + 1 < buf.length) {
      const i = Math.floor(readPos);
      const frac = readPos - i;
      const s = buf[i] * (1 - frac) + buf[i + 1] * frac;
      // 限幅到 [-1, 1] 再转 16bit
      const clamped = Math.max(-1, Math.min(1, s));
      pending.push(clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff);
      readPos += ratio;
    }

    // 保留未消费的尾部（含插值需要的前后各一个样本）
    const consumed = Math.floor(readPos);
    carry = buf.slice(consumed);
    readPos -= consumed;

    // 够一帧就回调，避免 latency 累积
    while (pending.length >= framesPerChunk) {
      const frame = pending.slice(0, framesPerChunk);
      pending = pending.slice(framesPerChunk);
      if (active) options.onChunk(Int16Array.from(frame));
    }
  };

  /* ---- 采集路径 ---- */
  let processor: ScriptProcessorNode | null = null;
  let workletNode: AudioWorkletNode | null = null;
  let stopped = false;

  const useWorklet = typeof ctx.audioWorklet !== 'undefined';

  if (useWorklet) {
    // AudioWorklet 需要外部脚本；用 Blob URL 内联，避免打包路径问题
    const code = `
      class LxCapture extends AudioWorkletProcessor {
        process(inputs) {
          const ch = inputs[0]?.[0];
          if (ch && ch.length) this.port.postMessage(ch.slice(0));
          return true;
        }
      }
      registerProcessor('lx-capture', LxCapture);
    `;
    const url = URL.createObjectURL(new Blob([code], { type: 'text/javascript' }));
    try {
      await ctx.audioWorklet.addModule(url);
      workletNode = new AudioWorkletNode(ctx, 'lx-capture');
      workletNode.port.onmessage = (e) => pushSamples(e.data as Float32Array);
      source.connect(workletNode);
      // worklet 不产生输出，但要接到 destination 才会驱动 process
      workletNode.connect(ctx.destination);
    } catch {
      // 加载失败就退回 ScriptProcessor
      workletNode = null;
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  if (!workletNode) {
    processor = ctx.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = (e) => {
      const ch = e.inputBuffer.getChannelData(0);
      pushSamples(new Float32Array(ch));
    };
    source.connect(processor);
    processor.connect(ctx.destination);
  }

  const cleanup = async (): Promise<void> => {
    if (stopped) return;
    stopped = true;
    active = false;
    try {
      source.disconnect();
      workletNode?.disconnect();
      if (processor) {
        processor.onaudioprocess = null;
        processor.disconnect();
      }
      stream.getTracks().forEach((t) => t.stop());
      await ctx.close();
    } catch {
      /* 释放失败不阻塞调用方 */
    }
  };

  return {
    get active() {
      return active;
    },
    stop: cleanup,
  };
}

/** 把麦克风的各类失败翻译成人话 */
function translateMicError(err: unknown): string {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return '你拒绝了麦克风权限，或当前环境不允许录音。请在浏览器权限设置里放行。';
      case 'NotFoundError':
        return '这台设备上找不到麦克风。';
      case 'NotReadableError':
        return '麦克风被其他程序占用了，关掉正在使用它的应用再试。';
      default:
        return `麦克风打开失败：${err.message}`;
    }
  }
  return `麦克风打开失败：${err instanceof Error ? err.message : String(err)}`;
}
